import { isGrantCurrent, type AdminUserSummary } from "@acruxsoft/platform-shared";

const EXPIRING_WINDOW_MS = 30 * 86400_000;

export interface LicenseBadge {
  appId: string;
  expiresAt: number;
  /** Runs out within 30 days — worth a call to renew. */
  expiringSoon: boolean;
}

export interface AdminRowSummary {
  /** Current licenses, soonest to expire first. */
  licenses: LicenseBadge[];
  pendingAppIds: string[];
  unverified: boolean;
  /**
   * A subscription from before licenses, still in its paid period. PayPal ones
   * (paid, not renewing) keep TVM until they lapse; a MANUAL- one left here
   * means the migration missed it.
   */
  legacySub: "paypal" | "manual" | null;
}

export type AdminFilter = "all" | "licensed" | "requested" | "expiring" | "unverified" | "legacy";

export const ADMIN_FILTERS: AdminFilter[] = ["all", "licensed", "requested", "expiring", "unverified", "legacy"];

export function adminRowSummary(u: AdminUserSummary, now: number = Date.now()): AdminRowSummary {
  const licenses = u.appGrants
    .filter((g) => isGrantCurrent(g, now))
    .map((g) => ({ appId: g.appId, expiresAt: g.expiresAt, expiringSoon: g.expiresAt - now <= EXPIRING_WINDOW_MS }))
    .sort((a, b) => a.expiresAt - b.expiresAt);

  // A cancelled sub still grants access until its paid period ends — the user
  // paid and just won't auto-renew.
  const inPeriod =
    u.subscriptionStatus === "active" ||
    u.subscriptionStatus === "past_due" ||
    (u.subscriptionStatus === "cancelled" && u.currentPeriodEnd != null && now < u.currentPeriodEnd);

  return {
    licenses,
    pendingAppIds: u.pendingRequestAppIds,
    unverified: !u.emailVerified,
    legacySub: inPeriod ? (u.isManual ? "manual" : "paypal") : null
  };
}

export function matchesAdminFilter(summary: AdminRowSummary, filter: AdminFilter): boolean {
  switch (filter) {
    case "all":
      return true;
    case "licensed":
      return summary.licenses.length > 0;
    case "requested":
      return summary.pendingAppIds.length > 0;
    case "expiring":
      return summary.licenses.some((l) => l.expiringSoon);
    case "unverified":
      return summary.unverified;
    case "legacy":
      return summary.legacySub !== null;
  }
}
